import { Caption, Text } from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { getCatalogTermsUrl, getShippingPartnerPolicyUrl } from '@/utils/urls';
import { getCatalogTitle } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getCheckoutShippingQuote } from '@/redux/modules/checkoutShippingQuote/checkoutShippingQuote.selectors';
import { getFeeError } from '@/redux/modules/processingFee';
import { getInvoiceById, getInvoiceSent, getIsInvoiceLoaded } from '@/redux/modules/checkout/checkout.selectors';
import { getPaymentError, getPaymentErrorCode, getPaymentSubmitted, getPaymentSuccess } from '@/redux/modules/payment';
import { Lock } from '@liveauctioneers/hammer-ui-core/icons';
import { ShippingMethods } from '@/enums/shippingMethod';
import { useAppSelector } from '@/redux/hooks';
import Button from '@liveauctioneers/caterwaul-components/lib/Button/Button';
import InvoiceSummaryList from './components/InvoiceSummaryList';
import InvoiceSummaryListRow from './components/InvoiceSummaryListRow';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import RowDivider from '../RowDivider';
import styled, { css } from 'styled-components';
import Throbber from '@liveauctioneers/caterwaul-components/lib/Throbber/Throbber';

export type InvoiceSummaryPayNowProps = {
    amountToPay: number; //cents
    amountToPayZip: number; //dollars
    catalogId: number;
    currency: string;
    handleSubmitClick: () => void;
    readOnly: boolean;
    readyToPay: boolean;
    shippingMethod: ShippingMethods;
};

const DOLLAR_AMMOUNT = 100;
const NUMBER_OF_PAYMENTS = 4;

const InvoiceSummaryPayNow = ({
    amountToPay,
    amountToPayZip,
    catalogId,
    currency,
    handleSubmitClick,
    readOnly,
    readyToPay,
    shippingMethod,
}: InvoiceSummaryPayNowProps) => {
    const catalogTitle = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const invoiceLoaded = useAppSelector((state) => getIsInvoiceLoaded(state, catalogId));
    const invoiceSent = useAppSelector((state) => getInvoiceSent(state, catalogId));
    const { invoice } = useAppSelector((state) => getInvoiceById(state, catalogId));
    const paymentSuccess = useAppSelector(getPaymentSuccess);
    const paymentSubmitted = useAppSelector(getPaymentSubmitted);
    const paymentErrorCode = useAppSelector(getPaymentErrorCode);
    const paymentError = useAppSelector(getPaymentError);
    const feeLoadingError = useAppSelector(getFeeError);
    const shippingQuote = useAppSelector(getCheckoutShippingQuote);

    const hasInfoText = Boolean(!readOnly || paymentErrorCode || paymentError || feeLoadingError);
    const getPolicies = () => {
        if (invoiceLoaded) {
            if (Number(shippingQuote?.total) > 0 && shippingMethod === ShippingMethods.LiveShipping) {
                return (
                    <FormattedMessage
                        id={
                            invoice.highRisk
                                ? 'checkout.balance.liveShippingHighRiskTerms'
                                : 'checkout.balance.liveShippingTerms'
                        }
                        values={{
                            shippingPolicies: (
                                <Caption key="checkout-balance-live-shipping-partners-policies-link">
                                    <Link
                                        target="_blank"
                                        to={getShippingPartnerPolicyUrl(shippingQuote.shippingProvider)}
                                    >
                                        <FormattedMessage id="checkout.balance.liveShippingPartnersPolicies" />
                                    </Link>
                                </Caption>
                            ),
                            terms: (
                                <Caption key="checkout-balance-terms-of-sale-link">
                                    <Link
                                        target="_blank"
                                        to={getCatalogTermsUrl(catalogId, catalogTitle)}
                                    >
                                        <FormattedMessage id="checkout.balance.termsOfSale" />
                                    </Link>
                                </Caption>
                            ),
                        }}
                    />
                );
            } else if (invoice.highRisk) {
                return (
                    <FormattedMessage
                        id="checkout.balance.highRiskTerms"
                        values={{
                            terms: (
                                <Caption key="checkout-balance-high-risk-terms-link">
                                    <Link
                                        target="_blank"
                                        to={getCatalogTermsUrl(catalogId, catalogTitle)}
                                    >
                                        <FormattedMessage id="checkout.balance.termsOfSale" />
                                    </Link>
                                </Caption>
                            ),
                        }}
                    />
                );
            }
        }
        return (
            <FormattedMessage
                id="checkout.balance.auctionHousePolicies"
                values={{
                    terms: (
                        <Caption key="checkout-balance-auction-house-policies-link">
                            <Link
                                target="_blank"
                                to={getCatalogTermsUrl(catalogId, catalogTitle)}
                            >
                                <FormattedMessage id="checkout.balance.termsOfSale" />
                            </Link>
                        </Caption>
                    ),
                }}
            />
        );
    };

    return (
        <InvoiceSummaryList>
            {invoiceLoaded ? (
                <StyledSpacer $hasInfoText={hasInfoText}>
                    <RowDivider />
                    {Boolean(amountToPayZip) ? (
                        <InvoiceSummaryListRow
                            amount={{
                                amount: paymentSuccess ? 0 : amountToPay / DOLLAR_AMMOUNT / NUMBER_OF_PAYMENTS,
                                currency,
                            }}
                            bold
                            label={<FormattedMessage id="checkout.balance.totalDueNow" />}
                        />
                    ) : (
                        <InvoiceSummaryListRow
                            amount={{
                                amount: paymentSuccess ? 0 : amountToPay / DOLLAR_AMMOUNT,
                                currency,
                            }}
                            bold
                            label={<FormattedMessage id="checkout.balance.total" />}
                        />
                    )}
                </StyledSpacer>
            ) : (
                <StyledThrobberWrapper>
                    <Throbber />
                </StyledThrobberWrapper>
            )}
            {amountToPay > 0 && invoiceSent && !paymentSuccess && (
                <StyledButtonContainer>
                    {Boolean(amountToPayZip) ? (
                        <StyledZipCheckoutButton
                            disabled={!readyToPay}
                            loading={paymentSubmitted}
                            onClick={handleSubmitClick}
                            primary
                            testid="zip-pay-now"
                        >
                            <FormattedMessage id="checkout.balance.checkoutWith" />
                            <StyledZipLogo src="https://p1.liveauctioneers.com/dist/images/zip-logo-reverse.svg" />
                        </StyledZipCheckoutButton>
                    ) : (
                        <StyledCheckoutButton
                            disabled={!readyToPay}
                            loading={paymentSubmitted}
                            onClick={handleSubmitClick}
                            primary
                            testid="pay-now"
                        >
                            <>
                                <Lock
                                    className="inline-flex"
                                    color="inverse"
                                    size="sm"
                                />
                                <FormattedMessage id="checkout.balance.cta" />
                            </>
                        </StyledCheckoutButton>
                    )}
                </StyledButtonContainer>
            )}
            {(Boolean(paymentErrorCode) || Boolean(paymentError) || Boolean(feeLoadingError)) && (
                <StyledErrorWrapper>
                    {/* default message is the error code because it looks better than a failed translation. */}
                    <Text
                        colorError
                        secondary
                    >
                        <FormattedMessage
                            defaultMessage={paymentErrorCode || paymentError || feeLoadingError}
                            id={`checkout.errorCodes.${paymentErrorCode}`}
                        />
                    </Text>
                </StyledErrorWrapper>
            )}
            {!readOnly && <Caption data-testid="checkout-policies">{getPolicies()}</Caption>}
        </InvoiceSummaryList>
    );
};

const StyledThrobberWrapper = styled.div`
    height: 35px;
    display: flex;
    justify-content: center;
`;

const StyledButtonContainer = styled.div``;

const StyledCheckoutButton = styled(Button)`
    width: 100%;
    margin: 10px 0 21px 0;
`;

const StyledZipCheckoutButton = styled(StyledCheckoutButton)`
    padding: 10px;
    background-color: ${({ theme }) => theme.colors.zip100};
    border-color: ${({ theme }) => theme.colors.zip100};

    &:hover,
    &:active {
        background-color: ${({ theme }) => theme.colors.zip200};
        border-color: ${({ theme }) => theme.colors.zip200};
    }

    &:disabled,
    &:active:disabled,
    &:hover:disabled {
        background-color: ${({ theme }) => theme.colors.zip100};
        border-color: ${({ theme }) => theme.colors.zip100};
        opacity: 60%;
    }
`;

const StyledZipLogo = styled.img`
    width: 50px;
    margin: 0 0 4px 6px;
`;

const StyledErrorWrapper = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 16px;
`;

const StyledSpacer = styled.div<{ $hasInfoText: boolean }>`
    margin: 5px 0 -5px 0;
    ${({ $hasInfoText }) => css`
        margin-bottom: ${$hasInfoText ? -5 : 16}px;
    `}
`;

export default InvoiceSummaryPayNow;
